import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, RouterStateSnapshot } from '@angular/router';
import { RoutePathsEnum } from '@app/app.utils';
import { Store } from '@ngrx/store';
import { selectPlatformDataLoaded } from '@shared/store/platform/platform.selectors';
import { setRedirectUrlAfterSignIn } from '@shared/store/user/user.action';
import { Observable, of } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { selectRedirectUrlAfterSignIn } from '../store/user/user.selectors';

@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate, CanActivateChild {
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly authService = inject(AuthService);

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | boolean {
    if (state.url.startsWith(RoutePathsEnum.Platform)) {
      return this.store.select(selectRedirectUrlAfterSignIn).pipe(
        take(1),
        switchMap((redirectUrlAfterSignIn) => {
          const token = this.authService.getToken();
          if (token == null) {
            // Save URL for deep linking
            this.store.dispatch(setRedirectUrlAfterSignIn({ redirectUrlAfterSignIn: state.url }));
            void this.router.navigate([RoutePathsEnum.Landing, RoutePathsEnum.Login]);
            return of(false);
          }
          if (redirectUrlAfterSignIn != null) {
            // Redirect deep linked page after logging in
            void this.router.navigateByUrl(redirectUrlAfterSignIn);
            this.store.dispatch(setRedirectUrlAfterSignIn({ redirectUrlAfterSignIn: null }));
            return of(true);
          }
          return this.store.select(selectPlatformDataLoaded).pipe(filter(Boolean), take(1));
        }),
        map(() => {
          return true;
        }),
      );
    }

    return false;
  }

  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | boolean {
    return this.canActivate(route, state);
  }
}
